<template>
  <div class="tw">
    <ContentSkeleton v-if="loading && !data" variant="list" :rows="4" />
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>
    <p v-else-if="!source" class="dw-empty">{{ t("noSources") }}</p>

    <!-- KPIs del período, comparados con el período anterior. -->
    <template v-else-if="view === 'productsSummary'">
      <div v-if="caption" class="tw-caption">{{ caption }}</div>
      <div class="tw-kpis">
        <div class="tw-kpi">
          <span class="tw-kpi__label">{{ t("kpi.products") }}</span>
          <span class="tw-kpi__value">{{ summary.products }}</span>
        </div>
        <div class="tw-kpi">
          <span class="tw-kpi__label">{{ t("kpi.operations") }}</span>
          <span class="tw-kpi__value">{{ summary.operations }}</span>
          <span v-if="summary.delta !== null" class="tw-kpi__delta" :class="summary.delta >= 0 ? 'text-success' : 'text-danger'">
            {{ summary.delta >= 0 ? "+" : "" }}{{ summary.delta }}% {{ t("vsPrevious") }}
          </span>
        </div>
        <div v-for="x in summary.qty" :key="'q' + x.unit" class="tw-kpi">
          <span class="tw-kpi__label">{{ t("kpi.qty") }} · {{ unitLabel(x.unit) }}</span>
          <span class="tw-kpi__value">{{ money(x.qty) }}</span>
        </div>
        <div v-for="x in summary.totals" :key="'a' + (x.currency || '')" class="tw-kpi">
          <span class="tw-kpi__label">{{ t("kpi.total") }} · {{ cur(x.currency) }}</span>
          <span class="tw-kpi__value">{{ money(x.amount) }}</span>
        </div>
        <div class="tw-kpi">
          <span class="tw-kpi__label">{{ t("kpi.newProducts") }}</span>
          <span class="tw-kpi__value">{{ summary.newProducts }}</span>
        </div>
      </div>
    </template>

    <!-- Listas: productos nuevos, clientes nuevos, clientes sin operaciones. -->
    <template v-else-if="isListView">
      <div v-if="caption" class="tw-caption">{{ caption }}</div>
      <p v-if="!listRows.length" class="dw-empty">{{ t(`empty.${view}`) }}</p>
      <ul v-else class="tw-list">
        <li v-for="r in listRows" :key="r.id" class="tw-row">
          <div class="tw-row__body">
            <router-link :to="r.to" class="tw-row__title">{{ r.label }}</router-link>
            <span class="tw-row__text">{{ r.text }}</span>
          </div>
          <span class="tw-row__value">{{ r.value }}</span>
        </li>
      </ul>
    </template>

    <!-- Concentración: cuánto se llevan los principales clientes. -->
    <template v-else-if="view === 'concentration'">
      <div v-if="caption" class="tw-caption">{{ caption }}</div>
      <p v-if="!concentration.total" class="dw-empty">{{ t("noData") }}</p>
      <template v-else>
        <div class="tw-big">
          <span class="tw-big__value">{{ concentration.share }}%</span>
          <span class="tw-big__label">{{ t("concentrationText", { n: concentration.n, total: concentration.clients }) }}</span>
        </div>
        <div class="tw-chart">
          <WidgetChart :type="chartType" :points="points" />
        </div>
      </template>
    </template>

    <!-- Gráficos: top de productos o contactos, por mes. -->
    <template v-else>
      <div v-if="caption" class="tw-caption">{{ caption }}</div>
      <p v-if="!points.length" class="dw-empty">{{ t("noData") }}</p>
      <div v-else class="tw-chart">
        <WidgetChart :type="chartType" :points="points" />
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Widgets «Productos» y «Contactos» del dashboard: los más vendidos, cantidades por
 * mes, principales clientes y proveedores, nuevos, dormidos, concentración. Todos leen
 * GET /api/trade-insights (una llamada por pantalla, tradeInsightsStore.js) y agrupan
 * según `entry.config.view`. Solo datos de las operaciones.
 *
 * Como en la ficha, cada widget muestra una fuente (contratos, embarques o proformas)
 * para no contar dos veces la proforma y su embarque, y cantidades por unidad y montos
 * por moneda sin sumarlos entre sí.
 */
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import WidgetChart from "@/components/gotrading/dashboard/widgets/WidgetChart.vue";
import { CHART_TYPES } from "@/components/gotrading/dashboard/widgetData";
import insightWidgetMixin, { MONTH_OPTIONS, LIMIT_OPTIONS } from "@/components/gotrading/item-profile/insight-widgets/insightWidgetMixin";
import { loadTradeInsights, invalidateTradeInsights } from "../tradeInsightsStore";

const LIST_LIMIT = 25;
const DORMANT_OPTIONS = [3, 6, 12];
const TOP_N_OPTIONS = [3, 5, 10];
const pad2 = (n) => String(n).padStart(2, "0");

/** Vista → qué filas mira: productos (lado elegido), compradores o proveedores. */
const BY_BUYER = ["topClients", "newClients", "dormant", "concentration"];
const BY_SELLER = ["topSuppliers"];

export default {
  name: "TradeWidget",
  components: { ContentSkeleton, WidgetChart },
  mixins: [insightWidgetMixin],
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return { data: null, loading: false, error: false };
  },
  computed: {
    view() {
      return this.config.view || (this.entry.config && this.entry.config.view) || "productsTop";
    },
    isListView() {
      return ["newProducts", "newClients", "dormant"].includes(this.view);
    },
    sources() {
      return (this.data && this.data.sources) || [];
    },
    source() {
      const keys = this.sources.map((s) => s.key);
      if (this.config.source && keys.includes(this.config.source)) return this.config.source;
      // Embarques primero: es la operación cerrada.
      return keys.includes("shipping") ? "shipping" : keys[0] || null;
    },
    all() {
      return ((this.data && this.data.rows) || []).filter((r) => r.s === this.source);
    },
    kinds() {
      const set = new Set(this.all.map((r) => r.k));
      return ["sale", "purchase", "trade"].filter((k) => set.has(k));
    },
    usesSide() {
      return !BY_BUYER.includes(this.view) && !BY_SELLER.includes(this.view) && this.kinds.length > 1;
    },
    side() {
      if (this.config.side && this.kinds.includes(this.config.side)) return this.config.side;
      return this.kinds.includes("sale") ? "sale" : this.kinds[0] || "sale";
    },
    /** Todas las filas de la fuente que cuentan para esta vista (sin cortar por período). */
    scoped() {
      if (BY_BUYER.includes(this.view)) return this.all.filter((r) => r.b);
      if (BY_SELLER.includes(this.view)) return this.all.filter((r) => r.v);
      return this.all.filter((r) => r.k === this.side);
    },
    /** Para el mixin (meses del eje con «todo»): fecha de cada fila. */
    ops() {
      return this.scoped.map((r) => ({ date: `${r.m}-01` }));
    },
    startMonth() {
      return this.monthsAgo(this.months ? this.months - 1 : null);
    },
    inPeriod() {
      const from = this.startMonth;
      return from ? this.scoped.filter((r) => r.m >= from) : this.scoped;
    },
    measure() {
      const m = this.config.measure;
      if (m === "amount" && this.finance) return "amount";
      return m === "operations" ? "operations" : "qty";
    },
    finance() {
      return !!(this.data && this.data.finance);
    },
    unitsAvailable() {
      const count = {};
      this.inPeriod.forEach((r) => (count[r.u || ""] = (count[r.u || ""] || 0) + 1));
      return Object.keys(count).sort((a, b) => count[b] - count[a]);
    },
    unit() {
      const u = this.config.unit;
      if (u !== undefined && this.unitsAvailable.includes(u)) return u;
      return this.unitsAvailable[0] || "";
    },
    currencies() {
      const uniq = Array.from(new Set(this.inPeriod.filter((r) => r.a != null).map((r) => r.c || null)));
      const sys = this.data && this.data.systemCurrency;
      return uniq.sort((a, b) => (a === sys ? -1 : b === sys ? 1 : 0));
    },
    currency() {
      if (this.config.currency !== undefined && this.currencies.includes(this.config.currency)) return this.config.currency;
      return this.currencies.length ? this.currencies[0] : null;
    },
    /** Lo que mide un gráfico o un ranking: cantidad en la unidad, monto en la moneda, operaciones. */
    measuredRows() {
      if (this.measure === "qty") return this.inPeriod.filter((r) => (r.u || "") === this.unit);
      if (this.measure === "amount") return this.inPeriod.filter((r) => r.a != null && (r.c || null) === this.currency);
      return this.inPeriod;
    },
    dormantMonths() {
      return DORMANT_OPTIONS.includes(this.config.dormant) ? this.config.dormant : 6;
    },
    topN() {
      return TOP_N_OPTIONS.includes(this.config.topN) ? this.config.topN : 5;
    },
    summary() {
      const rows = this.inPeriod;
      const ops = new Set();
      const products = new Set();
      const qty = {};
      const used = {};
      const totals = {};
      rows.forEach((r) => {
        r.o.forEach((o) => ops.add(o));
        products.add(r.p);
        qty[r.u || ""] = (qty[r.u || ""] || 0) + r.q;
        used[r.u || ""] = (used[r.u || ""] || 0) + 1;
        if (r.a != null) {
          const k = r.c || "";
          totals[k] = totals[k] || { currency: r.c || null, amount: 0 };
          totals[k].amount += r.a;
        }
      });
      // Período anterior del mismo largo, para comparar operaciones.
      let delta = null;
      if (this.months) {
        const prevFrom = this.monthsAgo(this.months * 2 - 1);
        const prevOps = new Set();
        this.scoped.filter((r) => r.m >= prevFrom && r.m < this.startMonth).forEach((r) => r.o.forEach((o) => prevOps.add(o)));
        if (prevOps.size) delta = Math.round(((ops.size - prevOps.size) / prevOps.size) * 100);
      }
      const firstByProduct = this.firstMonth("p");
      return {
        operations: ops.size,
        products: products.size,
        // Las dos unidades más usadas (no las de número más grande: 1 t pesa menos que 900 kg en la cuenta).
        qty: Object.keys(qty).sort((a, b) => used[b] - used[a]).slice(0, 2).map((unit) => ({ unit, qty: qty[unit] })),
        totals: Object.values(totals).slice(0, 2),
        newProducts: Array.from(products).filter((p) => !this.startMonth || firstByProduct[p] >= this.startMonth).length,
        delta,
      };
    },
    points() {
      if (this.view === "productsByMonth") {
        const rows = this.measure === "operations" ? this.distinctOps(this.measuredRows) : this.measuredRows;
        return this.perMonth(rows.map((r) => ({ month: r.m, value: this.valueOf(r) })));
      }
      if (this.view === "productsTop") return this.top(this.ranking("p", this.productName));
      if (this.view === "topClients") return this.top(this.ranking("b", this.contactName));
      if (this.view === "topSuppliers") return this.top(this.ranking("v", this.contactName));
      if (this.view === "concentration") {
        const ranked = this.ranking("b", this.contactName).sort((a, b) => b.value - a.value);
        const head = ranked.slice(0, this.topN);
        const rest = ranked.slice(this.topN).reduce((s, r) => s + r.value, 0);
        // Desde 100 sin decimales, como el resto de los rankings.
        const round = (v) => (Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100);
        return head.map((r) => ({ label: r.label, value: round(r.value) })).concat(rest ? [{ label: this.t("others"), value: round(rest), other: true }] : []);
      }
      return [];
    },
    concentration() {
      const ranked = this.ranking("b", this.contactName).sort((a, b) => b.value - a.value);
      const total = ranked.reduce((s, r) => s + r.value, 0);
      const head = ranked.slice(0, this.topN).reduce((s, r) => s + r.value, 0);
      return { total, clients: ranked.length, n: Math.min(this.topN, ranked.length), share: total ? Math.round((head / total) * 100) : 0 };
    },
    listRows() {
      if (this.view === "newProducts") {
        const first = this.firstMonth("p");
        return this.listOf("p", (id) => !this.startMonth || first[id] >= this.startMonth, (g) => ({
          label: this.productName(g.id),
          to: `/dynamic-model/Product/view/${g.id}`,
          text: this.t("firstIn", { month: this.monthLabel(first[g.id]) }),
          value: this.t("opsCount", { n: g.ops.size }),
          sort: first[g.id],
        }));
      }
      if (this.view === "newClients") {
        const first = this.firstMonth("b");
        return this.listOf("b", (id) => !this.startMonth || first[id] >= this.startMonth, (g) => ({
          label: this.contactName(g.id),
          to: `/dynamic-model/Contact/view/${g.id}`,
          text: this.t("firstIn", { month: this.monthLabel(first[g.id]) }),
          value: this.t("opsCount", { n: g.ops.size }),
          sort: first[g.id],
        }));
      }
      if (this.view === "dormant") {
        // Clientes que compraron alguna vez y no desde hace N meses: los de más operaciones primero.
        const limit = this.monthsAgo(this.dormantMonths - 1);
        const groups = {};
        this.scoped.forEach((r) => {
          const g = (groups[r.b] = groups[r.b] || { id: r.b, last: "", ops: new Set() });
          if (r.m > g.last) g.last = r.m;
          r.o.forEach((o) => g.ops.add(o));
        });
        return Object.values(groups)
          .filter((g) => g.last < limit)
          .sort((a, b) => b.ops.size - a.ops.size)
          .slice(0, LIST_LIMIT)
          .map((g) => ({
            id: g.id,
            label: this.contactName(g.id),
            to: `/dynamic-model/Contact/view/${g.id}`,
            text: this.t("lastIn", { month: this.monthLabel(g.last) }),
            value: this.t("opsCount", { n: g.ops.size }),
          }));
      }
      return [];
    },
    caption() {
      const parts = [];
      if (this.sources.length > 1) parts.push(this.t(`sources.${this.source}`));
      if (this.usesSide) parts.push(this.t(`kinds.${this.side}`));
      if (this.view === "dormant") parts.push(this.t("dormantFor", { n: this.dormantMonths }));
      else parts.push(this.months ? this.t("lastMonths", { n: this.months }) : this.t("allTime"));
      if (this.hasMeasure) {
        if (this.measure === "qty") parts.push(this.unitLabel(this.unit));
        else if (this.measure === "amount") parts.push(this.cur(this.currency));
        else parts.push(this.t("measure.operations"));
      }
      return parts.join(" · ");
    },
    hasMeasure() {
      return ["productsTop", "productsByMonth", "topClients", "topSuppliers", "concentration"].includes(this.view);
    },
    menuItems() {
      const items = [];
      if (["productsTop", "productsByMonth", "topClients", "topSuppliers", "concentration"].includes(this.view)) {
        items.push({ divider: true, key: "d-chart", label: this.$tn("dashboardWidgets.settings.chartType") });
        CHART_TYPES.forEach((k) => items.push({ key: `chart:${k}`, label: this.$tn(`dashboardWidgets.settings.type.${k}`), active: k === this.chartType }));
      }
      if (this.sources.length > 1) {
        items.push({ divider: true, key: "d-source", label: this.tn("menu.source") });
        this.sources.forEach((s) => items.push({ key: `source:${s.key}`, label: this.tn(`sources.${s.key}`), active: s.key === this.source }));
      }
      if (this.usesSide) {
        items.push({ divider: true, key: "d-side", label: this.tn("menu.side") });
        this.kinds.forEach((k) => items.push({ key: `side:${k}`, label: this.tn(`kinds.${k}`), active: k === this.side }));
      }
      if (this.hasMeasure) {
        items.push({ divider: true, key: "d-measure", label: this.tn("menu.measure") });
        ["qty", "amount", "operations"]
          .filter((k) => k !== "amount" || this.finance)
          .forEach((k) => items.push({ key: `measure:${k}`, label: this.tn(`measure.${k}`), active: k === this.measure }));
      }
      if (this.hasMeasure && this.measure === "qty" && this.unitsAvailable.length > 1) {
        items.push({ divider: true, key: "d-unit", label: this.tn("menu.unit") });
        this.unitsAvailable.forEach((u) => items.push({ key: `unit:${u}`, label: this.unitLabel(u), active: u === this.unit }));
      }
      if (this.hasMeasure && this.measure === "amount" && this.currencies.length > 1) {
        items.push({ divider: true, key: "d-cur", label: this.$tn("dashboardWidgets.finance.currency") });
        this.currencies.forEach((c) => items.push({ key: `currency:${c || ""}`, label: this.cur(c), active: c === this.currency }));
      }
      if (this.view === "dormant") {
        items.push({ divider: true, key: "d-dormant", label: this.tn("menu.dormant") });
        DORMANT_OPTIONS.forEach((n) => items.push({ key: `dormant:${n}`, label: this.tn("menu.months", { n }), active: n === this.dormantMonths }));
      } else {
        items.push({ divider: true, key: "d-months", label: this.tn("menu.period") });
        MONTH_OPTIONS.forEach((n) => items.push({ key: `months:${n}`, label: n ? this.tn("menu.months", { n }) : this.tn("menu.allTime"), active: n === this.months }));
      }
      if (["productsTop", "topClients", "topSuppliers"].includes(this.view)) {
        items.push({ divider: true, key: "d-limit", label: this.tn("menu.show") });
        LIMIT_OPTIONS.forEach((n) => items.push({ key: `limit:${n}`, label: this.tn("menu.top", { n }), active: n === this.limit }));
      }
      if (this.view === "concentration") {
        items.push({ divider: true, key: "d-topn", label: this.tn("menu.show") });
        TOP_N_OPTIONS.forEach((n) => items.push({ key: `topN:${n}`, label: this.tn("menu.top", { n }), active: n === this.topN }));
      }
      return items;
    },
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(items) {
        this.$emit("menu", items);
      },
    },
  },
  mounted() {
    this.load(false);
  },
  methods: {
    t(key, params) {
      return this.$t(`dashboardWidgets.trade.${key}`, params);
    },
    tn(key, params) {
      return this.$tn(`dashboardWidgets.trade.${key}`, params);
    },
    async load(force) {
      this.loading = true;
      this.error = false;
      try {
        this.data = await loadTradeInsights(force);
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    /** La grilla vuelve a montar el widget al «Actualizar»: que pida datos frescos. */
    beforeRefresh() {
      invalidateTradeInsights();
    },
    onMenu(key) {
      const [name, ...rest] = key.split(":");
      const value = rest.join(":");
      if (["months", "limit", "dormant", "topN"].includes(name)) this.$emit("update-config", { [name]: parseInt(value, 10) });
      else if (name === "currency") this.$emit("update-config", { currency: value || null });
      else if (["chart", "source", "side", "measure", "unit"].includes(name)) this.$emit("update-config", { [name]: value });
    },
    /** «YYYY-MM» de hace n meses (null = sin límite). */
    monthsAgo(n) {
      if (n == null) return null;
      const now = new Date();
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - n, 1));
      return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
    },
    valueOf(r) {
      if (this.measure === "amount") return r.a || 0;
      if (this.measure === "operations") return 1;
      return r.q;
    },
    /** Una fila por operación (para contar operaciones sin repetir). */
    distinctOps(rows) {
      const seen = new Set();
      const out = [];
      rows.forEach((r) => r.o.forEach((o) => {
        const k = `${o}|${r.m}`;
        if (seen.has(k)) return;
        seen.add(k);
        out.push({ ...r, o: [o] });
      }));
      return out;
    },
    /** Agrupa por `field` (p, b, v) según la medida elegida. */
    ranking(field, nameOf) {
      const map = {};
      if (this.measure === "operations") {
        const sets = {};
        this.measuredRows.forEach((r) => {
          if (!r[field]) return;
          sets[r[field]] = sets[r[field]] || new Set();
          r.o.forEach((o) => sets[r[field]].add(o));
        });
        Object.entries(sets).forEach(([id, s]) => (map[id] = s.size));
      } else {
        this.measuredRows.forEach((r) => {
          if (!r[field]) return;
          map[r[field]] = (map[r[field]] || 0) + this.valueOf(r);
        });
      }
      return Object.entries(map).map(([id, value]) => ({ label: nameOf(id), value }));
    },
    /** Primer mes en que aparece cada producto / contacto, en toda la historia. */
    firstMonth(field) {
      const first = {};
      this.scoped.forEach((r) => {
        const id = r[field];
        if (id && (!first[id] || r.m < first[id])) first[id] = r.m;
      });
      return first;
    },
    listOf(field, keep, toRow) {
      const groups = {};
      this.inPeriod.forEach((r) => {
        const id = r[field];
        if (!id || !keep(id)) return;
        const g = (groups[id] = groups[id] || { id, ops: new Set() });
        r.o.forEach((o) => g.ops.add(o));
      });
      return Object.values(groups)
        .map((g) => ({ id: g.id, ...toRow(g) }))
        .sort((a, b) => String(b.sort).localeCompare(String(a.sort)))
        .slice(0, LIST_LIMIT);
    },
    productName(id) {
      return (this.data && this.data.products && this.data.products[id]) || "—";
    },
    unitLabel(id) {
      return (id && this.data && this.data.units && this.data.units[id]) || this.t("noUnit");
    },
  },
};
</script>

<style scoped>
.tw { display: flex; flex-direction: column; gap: 8px; height: 100%; min-height: 0; }
.tw-caption { font-size: 0.75rem; color: var(--dw-muted); }
.tw-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 8px; }
.tw-kpi { display: flex; flex-direction: column; gap: 2px; padding: 8px 10px; border-radius: 8px; background: var(--dw-surface-2); min-width: 0; }
.tw-kpi__label { font-size: 0.72rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tw-kpi__value { font-size: 1.15rem; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tw-kpi__delta { font-size: 0.7rem; font-weight: 600; }
.tw-chart { flex: 1; min-height: 120px; }
.tw-list { list-style: none; margin: 0; padding: 0; overflow-y: auto; min-height: 0; }
.tw-row { display: flex; align-items: center; gap: 10px; padding: 6px 0; border-bottom: 1px solid var(--dw-border); }
.tw-row:last-child { border-bottom: 0; }
.tw-row__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.tw-row__title { font-weight: 600; font-size: 0.82rem; color: var(--dw-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
a.tw-row__title:hover { color: var(--bs-primary); }
.tw-row__text { font-size: 0.72rem; color: var(--dw-muted); }
.tw-row__value { font-size: 0.78rem; font-weight: 600; white-space: nowrap; color: var(--dw-muted); }
.tw-big { display: flex; align-items: baseline; gap: 8px; }
.tw-big__value { font-size: 1.8rem; font-weight: 700; color: var(--bs-primary); }
.tw-big__label { font-size: 0.78rem; color: var(--dw-muted); }
</style>
